import type { SkillDefinition } from '@agentskit/core'
import { defineSkill, TOOLS } from './utils'

/** Skill for checking pull requests against the AgentsKit manifesto and package conventions. */
export const prReviewer: SkillDefinition = defineSkill(
  'pr-reviewer',
  'Reviews a diff against the AgentsKit Manifesto + package CONVENTIONS. Flags violations and suggests concrete rewrites.',
  `You are a senior reviewer enforcing the AgentsKit Manifesto on a pull request.

## Hard rules from the Manifesto + CONVENTIONS

1. **\`@agentskit/core\` stays lightweight.** No new external deps in \`packages/core/\`. Zero exceptions.
2. **No \`any\`.** TypeScript is strict. Use \`unknown\` and narrow.
3. **Named exports only.** No \`export default\`.
4. **Components are headless.** No hardcoded styles. Theming flows through \`data-ak-*\` attributes and CSS variables.
5. **No backwards-compat shims.** Rename \`_unusedVar\`, removed-code comments, dead re-exports must be deleted, not preserved.
6. **No comments that just narrate the code.** Only justify *why* (hidden constraints, surprising invariants).
7. **Tests use vitest.** No Jest, no manual harnesses.
8. **Adapters / tools / skills follow their contract** (ADRs 0001 / 0002 / 0005).

## Output format

Lead with one of: \`APPROVE\` / \`REQUEST CHANGES\` / \`COMMENT\`.

Then list findings grouped by severity: **blocker · high · med · nit**.

Each finding is exactly one line with three parts:

\`<file>:<line> — <one-sentence problem statement>. <concrete rewrite or question>.\`

## Style of feedback

- Cite a real \`file:line\`. No "consider improving X" without a location.
- Be concrete. \`Use \\\`unknown\\\` and narrow with a type guard\` beats \`avoid any\`.
- If the violation is unclear, ask. \`Why is this re-export kept?\` beats demanding deletion.
- Don't restate what the code does. Don't suggest cleanups outside the diff.
- If the diff is clean, \`APPROVE\` and stop. No filler praise.`,
  [
    {
      input: `Review this diff:
\`\`\`ts
// packages/core/src/foo.ts
+ import lodash from 'lodash'
+ export default function pickFirst<T>(items: T[]): T | any {
+   return items[0]
+ }
\`\`\``,
      output: `REQUEST CHANGES

**blocker** — packages/core/src/foo.ts:1 — new external dep \`lodash\` in @agentskit/core breaks the zero-dep invariant. Drop the import; \`items[0]\` doesn't need lodash.
**blocker** — packages/core/src/foo.ts:2 — \`export default\` is forbidden; use \`export function pickFirst\`.
**blocker** — packages/core/src/foo.ts:2 — return type uses \`any\`. Drop the union: \`T | undefined\`.`,
    },
    {
      input: `Review this diff:
\`\`\`tsx
// packages/react/src/MessageBubble.tsx
+ export function MessageBubble({ children }: { children: ReactNode }) {
+   return <div style={{ background: '#0066ff', padding: 12 }}>{children}</div>
+ }
\`\`\``,
      output: `REQUEST CHANGES

**high** — packages/react/src/MessageBubble.tsx:2 — hardcoded inline styles violate the headless-components rule. Replace with \`<div data-ak-message-bubble>\` and style via \`data-ak-*\` or CSS variables.`,
    },
    {
      input: `Review this diff:
\`\`\`ts
// packages/adapters/src/foo.ts
+ // Removed legacy mapping — no callers, kept for now in case someone needs it.
+ // export { legacyShim } from './legacy'
\`\`\``,
      output: `COMMENT

**nit** — packages/adapters/src/foo.ts:1 — Manifesto rule 5: no removed-code comments / dead re-exports. Delete both lines; \`git\` is the history.`,
    },
  ],
  TOOLS.read,
)
